// src/modules/competitions/components/ParticipantCard.tsx

import React, { useState, useRef, useEffect } from 'react';
import { getFullImageUrl } from '../../../shared/services/apiConfig';
import Button from '../../../shared/components/ui/Button';
import '../styles/participantCard.css';

interface ParticipantCardProps {
  participant: {
    participant_id: number;
    participant_code: string;
    level: string;
    registration_date: string;
    statistics: {
      total_items: number;
      evaluated_items: number;
      pending_items: number;
      is_fully_evaluated: boolean;
    };
    first_image_url: string | null;
  };
  onEvaluate: (participantId: number) => void;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({
  participant,
  onEvaluate,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // ✅ OPTIMIZACIÓN 1: Intersection Observer para lazy loading
  useEffect(() => {
    if (!cardRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, []);

  const { statistics } = participant;
  const progressPercentage =
    statistics.total_items > 0
      ? (statistics.evaluated_items / statistics.total_items) * 100
      : 0;

  // ✅ OPTIMIZACIÓN 2: URL con parámetros de optimización
  const getOptimizedImageUrl = (url: string | null) => {
    if (!url) return null;
    
    const fullUrl = getFullImageUrl(url);
    if (!fullUrl) return null;
    
    const separator = fullUrl.includes('?') ? '&' : '?';
    return `${fullUrl}${separator}webp=1&thumb=1`;
  };

  const imageUrl = getOptimizedImageUrl(participant.first_image_url);

  return (
    <div 
      ref={cardRef} 
      className="participant-card"
      onMouseEnter={() => setShowOverlay(true)}
      onMouseLeave={() => setShowOverlay(false)}
      onClick={() => onEvaluate(participant.participant_id)}
    >
      {/* Badge con código - FUERA del background */}
      <div className="participant-card__badge">
        {participant.participant_code}
      </div>

      {/* Indicador de estado - FUERA del background */}
      <div className={`participant-card__status ${statistics.is_fully_evaluated ? 'completed' : 'pending'}`}>
        {statistics.pending_items}
      </div>

      {/* Imagen de fondo */}
      <div className="participant-card__background">
        {isVisible && imageUrl ? (
          <img
            src={imageUrl}
            alt={participant.participant_code}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const originalUrl = getFullImageUrl(participant.first_image_url);
              if (originalUrl && target.src !== originalUrl) {
                target.src = originalUrl;
              } else {
                setImageLoaded(true);
              }
            }}
            loading="lazy"
            decoding="async"
            style={{ 
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease'
            }}
          />
        ) : (
          <div className="participant-card__placeholder">
            📷
          </div>
        )}
      </div>

      {/* Overlay con información - SEPARADO del background */}
      {showOverlay && (
        <div className="participant-card__overlay">
          <div className="participant-card__overlay-content">
            {/* Sección superior */}
            <div>
              <h3 className="participant-card__code">
                {participant.participant_code}
              </h3>

              {/* Estadísticas en grid 2x2 */}
              <div className="participant-card__stats">
                <div className="stat-item">
                  <span className="stat-icon">📋</span>
                  <span className="stat-text">
                    {statistics.total_items} items
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">✅</span>
                  <span className="stat-text">
                    {statistics.evaluated_items} eval.
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">⏳</span>
                  <span className="stat-text">
                    {statistics.pending_items} pend.
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">🎯</span>
                  <span className="stat-text">
                    {participant.level}
                  </span>
                </div>
              </div>
            </div>

            {/* Sección inferior */}
            <div>
              {/* Barra de progreso */}
              {!statistics.is_fully_evaluated && (
                <div className="participant-card__progress">
                  <div className="progress-bar">
                    <div
                      className="progress-bar__fill"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {Math.round(progressPercentage)}% completado
                  </span>
                </div>
              )}

              {/* Estado completado o botón de evaluar */}
              {statistics.is_fully_evaluated ? (
                <div className="participant-card__completed">
                  <span className="completed-icon">✓</span>
                  Evaluación completa
                </div>
              ) : (
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    onEvaluate(participant.participant_id);
                  }}
                >
                  Evaluar ahora
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantCard;